import { get, put } from '@/api/client';
import { useSales } from '@/api/labour';
import { useSetupDrafts } from '@/api/onboarding';
import { useUsers } from '@/api/people';
import { useCan } from '@/auth/caps';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { usePowerOffice } from '@/pages/lonn/PowerOffice';
import { useTripletex } from '@/pages/lonn/Tripletex';
/**
 * Oppsett og oppfølging above the categories (#267, Settings V1.1): one required correction and one
 * optional improvement from the company's saved data (setupFacts.ts), each opening its exact task.
 * Nothing shows when nothing is missing, so ordinary Settings is immediately usable. Required is told
 * by an icon and amber words, never a coloured stripe (owner, 2026-10-28). Optional guidance starts
 * closed on a phone.
 */
import { systemOf } from '@/pages/lonn/payrollSystem';
import { today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { toast } from '@/ui';
import { Icon } from '@/ui/icons';
import { type UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { type Dismissed, type FollowUps, type Load, type Task, followUps, monthBefore } from './setupFacts';

const load = <X, Y>(q: UseQueryResult<X>, f: (x: X) => Y): Load<Y> =>
  q.isError
    ? { state: 'failed' }
    : q.data !== undefined
      ? { state: 'ready', data: f(q.data) }
      : { state: 'pending' };

function monthEnd(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y ?? 2026, m ?? 1, 0)).toISOString().slice(0, 10);
}

export function useFollowUps() {
  const { tenant, workspace } = useTenant();
  const can = useCan();
  const admin = tenant?.me?.is_admin === true;
  const sys = systemOf(tenant);
  const usersQ = useUsers();
  const tx = useTripletex(admin && sys === 'tripletex');
  const po = usePowerOffice(admin && sys === 'poweroffice');
  const lastMonth = monthBefore(today());
  const kost = can('vaktplan.kost') && workspace.enabled.scheduling;
  const salesQ = useSales(`${lastMonth}-01`, monthEnd(lastMonth));
  const linkQ = sys === 'tripletex' ? tx : sys === 'poweroffice' ? po : null;
  const drafts = useSetupDrafts(admin);
  const result = followUps({
    linkDraft: drafts.data?.find((d) => d.setup === 'lonnskobling') ?? null,
    people: can('ansatte.endre') ? load(usersQ, (x) => x) : null,
    regularPayCode: admin && tenant ? { state: 'ready', data: tenant.regular_pay_code ?? null } : null,
    link:
      admin && linkQ
        ? load(linkQ as UseQueryResult<{ available: boolean; connected: boolean }>, (d) => ({
            available: d.available,
            connected: d.connected,
            system: sys === 'poweroffice' ? 'PowerOffice Go' : 'Tripletex',
          }))
        : null,
    sales: kost
      ? salesQ.failed
        ? { state: 'failed' }
        : salesQ.ready
          ? { state: 'ready', data: Object.values(salesQ.sales).filter((v) => v != null).length }
          : { state: 'pending' }
      : null,
    lastMonth,
  });
  const retry = () => {
    if (usersQ.isError) usersQ.refetch();
    if (linkQ?.isError) linkQ.refetch();
    if (salesQ.failed) salesQ.retry();
  };
  return { ...result, retry };
}

function TaskBody({ t, onLater }: { t: Task; onLater?: () => void }) {
  return (
    <>
      <p className="stfu-line">{t.line}</p>
      <p className="stfu-acts">
        <Link className="mlink" to={t.to} data-testid={`followup-go-${t.id}`}>
          {t.action}
          <Icon name="arrowRight" />
        </Link>
        {onLater && (
          <button type="button" className="mlink stfu-later" onClick={onLater}>
            {T('Senere')}
          </button>
        )}
      </p>
    </>
  );
}

const NONE: Dismissed = { hidden: false, later: [] };

/**
 * What this person put away, on the server per company and person (GET/PUT /settings/followup, #267 S2),
 * so another device shows the same. Keyed by company and person: a switch never reads another's choice.
 * Until it has loaded, nothing is hidden; a failed save keeps the choice on this page and says so.
 */
function useDismissed(tenantId: number | undefined, userId: number | undefined) {
  const qc = useQueryClient();
  const key = ['settings-followup', tenantId, userId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => get<Dismissed>('/settings/followup'),
    enabled: tenantId != null && userId != null,
  });
  const d: Dismissed = {
    hidden: q.data?.hidden === true,
    later: Array.isArray(q.data?.later) ? q.data.later : [],
  };
  const save = (next: Dismissed) => {
    qc.setQueryData(key, next);
    put<Dismissed>('/settings/followup', next).catch(() =>
      toast(T('Valget ble ikke lagret. Det gjelder bare til siden lastes på nytt.')),
    );
  };
  return [q.data ? d : NONE, save] as const;
}

export function FollowUp() {
  const { tenant } = useTenant();
  const userId = useAuth((s) => s.user?.id);
  const [dismissed, onDismiss] = useDismissed(tenant?.id, userId);
  return <FollowUpBlock {...useFollowUps()} dismissed={dismissed} onDismiss={onDismiss} />;
}

/** The block itself, from the tasks: what Storybook shows (Page elements/Innstillinger). */
export function FollowUpBlock({
  required,
  optional,
  unknown,
  retry,
  dismissed: d,
  onDismiss,
}: FollowUps & { retry: () => void; dismissed: Dismissed; onDismiss: (d: Dismissed) => void }) {
  const [optOpen, setOptOpen] = useState(() => !window.matchMedia?.('(max-width: 800px)').matches);
  const opts = optional.filter((t) => !d.later.includes(t.id));
  const req = required[0];
  const opt = opts[0];
  if (!req && !opt && !unknown.length) return null;
  const count = [
    required.length
      ? T(required.length === 1 ? '{n} nødvendig' : '{n} nødvendige', { n: required.length })
      : null,
    opts.length ? T(opts.length === 1 ? '{n} valgfri' : '{n} valgfrie', { n: opts.length }) : null,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <section className="stfu" aria-labelledby="stfu-h" data-testid="settings-followup">
      <div className="stfu-head">
        <div className="stfu-tt">
          <h2 id="stfu-h">{T('Oppsett og oppfølging')}</h2>
          {count && <span className="stfu-n">{count}</span>}
        </div>
        {(req || opt) && (
          <button
            type="button"
            className="mlink stfu-hide"
            aria-expanded={!d.hidden}
            aria-controls="stfu-body"
            onClick={() => onDismiss({ ...d, hidden: !d.hidden })}
          >
            {d.hidden ? T('Vis') : T('Skjul')}
            <Icon name="chevronDown" />
          </button>
        )}
      </div>
      {!d.hidden && (
        <div className="stfu-grid" id="stfu-body">
          {req && (
            <article className="stfu-card req" data-testid={`followup-${req.id}`}>
              <p className="stfu-eye">
                <Icon name="warn" />
                {req.eyebrow}
              </p>
              <h3>{req.title}</h3>
              <TaskBody t={req} />
            </article>
          )}
          {opt && (
            <details
              className="stfu-card opt"
              open={optOpen}
              onToggle={(e) => setOptOpen((e.target as HTMLDetailsElement).open)}
              data-testid={`followup-${opt.id}`}
            >
              <summary>
                <span className="stfu-eye">
                  <Icon name="info" />
                  {opt.eyebrow}
                </span>
                <span className="stfu-title">{opt.title}</span>
                <Icon name="chevronDown" />
              </summary>
              <TaskBody t={opt} onLater={() => onDismiss({ ...d, later: [...d.later, opt.id] })} />
            </details>
          )}
        </div>
      )}
      {unknown.length > 0 && (
        <output className="stfu-unk">
          <Icon name="warn" />
          <span>
            {T('Oppfølgingen er ufullstendig: {what} kunne ikke leses.', { what: unknown.join(', ') })}
          </span>
          <button type="button" className="mlink" onClick={retry}>
            {T('Prøv igjen')}
          </button>
        </output>
      )}
    </section>
  );
}
